import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useIEPGoals } from 'context/IEPGoalsContext';
import { useIncidents } from 'context/IncidentsContext';
import {
  useUserProfiles,
  shortRoleLabel,
  roleColorClasses,
} from 'context/UserProfilesContext';
import { deriveNameFromEmail } from 'lib/currentUser';
import { humanizeSupabaseError } from '../../../lib/supabaseErrors';

// ---------------------------------------------------------------------------
// GoalDetailView — real, Supabase-backed detail modal for a single IEP goal.
//
// This replaces the earlier prototype (mock objectives + fake timeline) with
// a small focused workflow:
//
//   1. A log-a-data-point form. Value, date, optional level of support and
//      notes. Writes to public.goal_progress_entries.
//   2. A progress-over-time chart. Reads back the entries, plots value vs.
//      target, and shades the target as a reference line.
//   3. A recent-entries table with delete.
//
// The DB trigger in supabase/migrations/0005_goal_progress_entries.sql
// auto-recomputes behavior_goals.progress_percent whenever an entry is
// added/updated/deleted, so the goal card reflects the latest data point
// without any client-side scheming.
// ---------------------------------------------------------------------------

const MEASUREMENT_TYPE_OPTIONS = [
  { value: 'percent_correct', label: 'Percent correct' },
  { value: 'frequency', label: 'Frequency (count)' },
  { value: 'duration', label: 'Duration' },
  { value: 'rate', label: 'Rate (per unit time)' },
  { value: 'accuracy_ratio', label: 'Accuracy (X out of Y)' },
  { value: 'independence_level', label: 'Independence level' },
  { value: 'other', label: 'Other' },
];

const SUPPORT_LEVEL_OPTIONS = [
  { value: 'independent', label: 'Independent' },
  { value: 'verbal_prompt', label: 'Verbal prompt' },
  { value: 'visual_prompt', label: 'Visual prompt' },
  { value: 'gestural_prompt', label: 'Gestural prompt' },
  { value: 'physical_prompt', label: 'Physical prompt' },
  { value: 'full_assistance', label: 'Full assistance' },
];

const SUPPORT_LABEL = SUPPORT_LEVEL_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function formatShortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Build a stable timestamp key for sorting when observed_on dates collide.
function entrySortKey(e) {
  const d = e?.observedOn || '';
  const c = e?.createdAt || '';
  return `${d}T${c}`;
}

// Assemble chart data (chronological). Fills a `target` series from the
// entry-level target when set, else the goal-level target. Recharts skips
// null values, so an entry without an explicit target still shows the goal
// baseline as the dashed line.
function buildChartData(entries, goalTarget) {
  const sorted = [...entries].sort((a, b) =>
    entrySortKey(a).localeCompare(entrySortKey(b)),
  );
  return sorted.map((e) => {
    const effectiveTarget =
      e.targetValue != null && e.targetValue !== ''
        ? Number(e.targetValue)
        : goalTarget;
    return {
      date: formatShortDate(e.observedOn),
      value: e.value == null ? null : Number(e.value),
      target: effectiveTarget == null ? null : Number(effectiveTarget),
      supportLevel: e.supportLevel || null,
      notes: e.notes || '',
    };
  });
}

const GoalDetailView = ({ goal, studentName, onClose }) => {
  const { listProgressEntries, logProgress, removeProgressEntry, isLive } =
    useIEPGoals();
  const { getUserById } = useUserProfiles();
  const { incidentsForGoal } = useIncidents();

  // Incidents the teacher (or a para) tagged as data for this goal via
  // the "Does this relate to an IEP goal?" chip picker in the incident
  // form. Sorted newest-first, capped at 6 so the detail view stays
  // scannable; the full list lives on the Behavior Incidents page.
  const linkedIncidents = useMemo(() => {
    const list = incidentsForGoal(goal?.id) || [];
    return [...list]
      .sort((a, b) => {
        const aTime = a.occurredAt ? new Date(a.occurredAt).getTime() : 0;
        const bTime = b.occurredAt ? new Date(b.occurredAt).getTime() : 0;
        return bTime - aTime;
      })
      .slice(0, 6);
  }, [incidentsForGoal, goal?.id]);

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [form, setForm] = useState({
    observedOn: todayIsoDate(),
    value: '',
    targetValue: '',
    unit: goal?.unit || '',
    measurementType: '',
    supportLevel: '',
    notes: '',
  });
  const [formError, setFormError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    if (!goal?.id) return;
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await listProgressEntries(goal.id);
      setEntries(rows);
    } catch (err) {
      setLoadError(
        humanizeSupabaseError(
          err,
          'Could not load progress data for this goal.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [goal?.id, listProgressEntries]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep the "unit" field of the log form in sync when the goal's unit
  // changes (e.g. the parent modal updated it while this view is open).
  useEffect(() => {
    setForm((f) => ({ ...f, unit: f.unit || goal?.unit || '' }));
  }, [goal?.unit]);

  const chartData = useMemo(
    () =>
      buildChartData(
        entries,
        goal?.targetValue == null ? null : Number(goal.targetValue),
      ),
    [entries, goal?.targetValue],
  );

  const latestEntry = useMemo(() => {
    if (entries.length === 0) return null;
    const sorted = [...entries].sort((a, b) =>
      entrySortKey(b).localeCompare(entrySortKey(a)),
    );
    return sorted[0];
  }, [entries]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (form.value === '' || form.value == null) {
      setFormError('Enter the measured value.');
      return;
    }
    const numValue = Number(form.value);
    if (Number.isNaN(numValue)) {
      setFormError('Value must be a number.');
      return;
    }
    if (form.targetValue !== '' && Number.isNaN(Number(form.targetValue))) {
      setFormError('Target value must be a number if provided.');
      return;
    }

    setIsSaving(true);
    try {
      await logProgress(goal.id, {
        studentId: goal.studentId,
        observedOn: form.observedOn || todayIsoDate(),
        value: numValue,
        targetValue:
          form.targetValue === '' || form.targetValue == null
            ? null
            : Number(form.targetValue),
        unit: form.unit || goal?.unit || null,
        measurementType: form.measurementType || null,
        supportLevel: form.supportLevel || null,
        notes: form.notes.trim() || null,
      });
      setForm({
        observedOn: todayIsoDate(),
        value: '',
        targetValue: '',
        unit: form.unit || goal?.unit || '',
        measurementType: form.measurementType,
        supportLevel: form.supportLevel,
        notes: '',
      });
      await load();
    } catch (err) {
      setFormError(
        humanizeSupabaseError(
          err,
          'Could not save this data point. Please try again.',
        ),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!entry?.id) return;
    const confirmed = window.confirm(
      `Delete the data point from ${formatShortDate(entry.observedOn)}?\n\nThis cannot be undone and progress % will recalculate.`,
    );
    if (!confirmed) return;
    try {
      await removeProgressEntry(entry.id, goal.id);
      await load();
    } catch (err) {
      setLoadError(
        humanizeSupabaseError(
          err,
          'Could not delete this data point. Please try again.',
        ),
      );
    }
  };

  const unitLabel = goal?.unit || form.unit || '';

  // Single-scroll-container pattern (see AddStudentModal for rationale).
  // Prevents the iOS Safari nested-scroll bug where the user can't
  // reach the top of a tall modal.
  const overlayDismiss = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[1100] bg-black/40 overflow-y-auto overscroll-contain"
      onClick={overlayDismiss}
    >
      <div
        className="min-h-full flex items-start sm:items-center justify-center p-4"
        onClick={overlayDismiss}
      >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="goal-detail-title"
        className="w-full max-w-4xl rounded-xl bg-white shadow-xl my-4 sm:my-8"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-gray-200 bg-white p-5 rounded-t-xl">
          <div className="min-w-0">
            <h2
              id="goal-detail-title"
              className="text-lg font-semibold text-gray-900"
            >
              Goal progress
            </h2>
            <p className="mt-0.5 text-sm text-gray-500 line-clamp-2">
              {goal?.specific || '(no goal statement)'}
            </p>
            <p className="mt-1 text-xs text-gray-400">
              {studentName || 'Unnamed student'} •{' '}
              {isLive ? 'Live sync' : 'Demo mode (local only)'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6 p-5 lg:grid-cols-5">
          {/* Log a data point (left column on desktop) */}
          <form
            onSubmit={handleSubmit}
            className="lg:col-span-2 rounded-lg border border-gray-200 bg-gray-50 p-4"
          >
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              Log a data point
            </h3>

            {formError && (
              <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700 whitespace-pre-line">
                {formError}
              </div>
            )}

            <div className="grid grid-cols-1 gap-3">
              <Input
                label="Date"
                type="date"
                value={form.observedOn}
                onChange={(e) => handleChange('observedOn', e?.target?.value)}
                required
              />

              <div className="grid grid-cols-2 gap-3">
                <Input
                  label={`Value${unitLabel ? ` (${unitLabel})` : ''}`}
                  type="number"
                  step="any"
                  value={form.value}
                  onChange={(e) => handleChange('value', e?.target?.value)}
                  placeholder="e.g. 62"
                  required
                />
                <Input
                  label="Target (optional)"
                  type="number"
                  step="any"
                  value={form.targetValue}
                  onChange={(e) =>
                    handleChange('targetValue', e?.target?.value)
                  }
                  placeholder={
                    goal?.targetValue == null
                      ? 'e.g. 80'
                      : String(goal.targetValue)
                  }
                  description={
                    goal?.targetValue == null
                      ? undefined
                      : 'Leave blank to use the goal-level target.'
                  }
                />
              </div>

              {!goal?.unit && (
                <Input
                  label="Unit (optional)"
                  value={form.unit}
                  onChange={(e) => handleChange('unit', e?.target?.value)}
                  placeholder='e.g. "%", "wpm", "minutes"'
                />
              )}

              <Select
                label="Measurement type"
                placeholder="Choose…"
                options={MEASUREMENT_TYPE_OPTIONS}
                value={form.measurementType}
                onChange={(value) => handleChange('measurementType', value)}
              />

              <Select
                label="Level of support"
                placeholder="Choose…"
                options={SUPPORT_LEVEL_OPTIONS}
                value={form.supportLevel}
                onChange={(value) => handleChange('supportLevel', value)}
              />

              <div>
                <label
                  htmlFor="entry-notes"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Notes (optional)
                </label>
                <textarea
                  id="entry-notes"
                  value={form.notes}
                  onChange={(e) => handleChange('notes', e?.target?.value)}
                  rows={3}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  placeholder="What did you observe? Any context worth remembering?"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-1">
                <Button
                  type="submit"
                  loading={isSaving}
                  iconName={isSaving ? 'Loader2' : 'Save'}
                  iconPosition="left"
                >
                  {isSaving ? 'Saving…' : 'Save data point'}
                </Button>
              </div>
            </div>
          </form>

          {/* Chart + summary (right column on desktop) */}
          <div className="lg:col-span-3 space-y-4">
            {/* Summary tiles */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <div className="text-xs text-gray-500">Progress</div>
                <div className="text-xl font-bold text-gray-900">
                  {goal?.progressPercent == null
                    ? '—'
                    : `${goal.progressPercent}%`}
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <div className="text-xs text-gray-500">Data points</div>
                <div className="text-xl font-bold text-gray-900">
                  {entries.length}
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <div className="text-xs text-gray-500">Latest value</div>
                <div className="text-xl font-bold text-gray-900">
                  {latestEntry?.value == null
                    ? '—'
                    : `${latestEntry.value}${unitLabel ? ` ${unitLabel}` : ''}`}
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <div className="text-xs text-gray-500">Target</div>
                <div className="text-xl font-bold text-gray-900">
                  {goal?.targetValue == null
                    ? '—'
                    : `${goal.targetValue}${unitLabel ? ` ${unitLabel}` : ''}`}
                </div>
              </div>
            </div>

            {/* Chart */}
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">
                  Progress over time
                </h3>
                {chartData.length > 0 && (
                  <span className="text-xs text-gray-500">
                    Showing {chartData.length} data point
                    {chartData.length === 1 ? '' : 's'}
                  </span>
                )}
              </div>
              {loadError ? (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 whitespace-pre-line">
                  {loadError}
                </div>
              ) : loading ? (
                <div className="flex items-center justify-center py-10 text-sm text-gray-500">
                  <Icon
                    name="Loader2"
                    size={16}
                    className="animate-spin mr-2"
                  />
                  Loading data…
                </div>
              ) : chartData.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500">
                  <Icon
                    name="LineChart"
                    size={40}
                    className="mx-auto mb-2 opacity-40"
                  />
                  No data yet. Log a data point on the left to start
                  tracking progress.
                </div>
              ) : (
                <div style={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer>
                    <LineChart
                      data={chartData}
                      margin={{ top: 8, right: 20, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="var(--color-border)"
                      />
                      <XAxis
                        dataKey="date"
                        stroke="var(--color-muted-foreground)"
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis
                        stroke="var(--color-muted-foreground)"
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'var(--color-popover)',
                          border: '1px solid var(--color-border)',
                          borderRadius: '8px',
                          fontSize: 12,
                        }}
                        formatter={(v, name) => [
                          v == null
                            ? '—'
                            : `${v}${unitLabel ? ` ${unitLabel}` : ''}`,
                          name === 'value' ? 'Observed' : 'Target',
                        ]}
                      />
                      <Legend
                        formatter={(name) =>
                          name === 'value' ? 'Observed' : 'Target'
                        }
                      />
                      {goal?.targetValue != null && (
                        <ReferenceLine
                          y={Number(goal.targetValue)}
                          stroke="var(--color-secondary)"
                          strokeDasharray="4 4"
                          label={{
                            value: 'Target',
                            position: 'right',
                            fill: 'var(--color-secondary)',
                            fontSize: 11,
                          }}
                        />
                      )}
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="var(--color-primary)"
                        strokeWidth={2}
                        dot={{
                          fill: 'var(--color-primary)',
                          strokeWidth: 2,
                          r: 4,
                        }}
                        connectNulls
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Entries list */}
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">
                Recent data points
              </h3>
              {entries.length === 0 ? (
                <p className="text-sm text-gray-500">
                  Data points you log will appear here.
                </p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {entries.slice(0, 8).map((entry) => {
                    const actor = getUserById(entry.recordedBy);
                    const actorName =
                      actor?.displayName ||
                      deriveNameFromEmail(actor?.email) ||
                      (entry.recordedBy ? 'A teammate' : null);
                    const short = shortRoleLabel(actor);
                    const actorColor = roleColorClasses(actor);
                    // Student self check-ins (migration 0012) get a
                    // distinctive indigo pill + emoji so the teacher
                    // can instantly tell "this data came from the
                    // student, not from an adult observation." The
                    // rating and feeling are surfaced as chips.
                    const isSelfCheckIn = entry.source === 'student_self';
                    const ratingLabel =
                      entry.studentRating === 'met'
                        ? 'I did it!'
                        : entry.studentRating === 'partial'
                          ? 'Kind of'
                          : entry.studentRating === 'not_met'
                            ? 'Not today'
                            : null;
                    const ratingChipClass =
                      entry.studentRating === 'met'
                        ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                        : entry.studentRating === 'partial'
                          ? 'bg-amber-100 text-amber-800 border-amber-200'
                          : entry.studentRating === 'not_met'
                            ? 'bg-rose-100 text-rose-800 border-rose-200'
                            : '';
                    const feelingEmoji =
                      entry.feeling === 'blue'
                        ? '💧'
                        : entry.feeling === 'green'
                          ? '🌱'
                          : entry.feeling === 'yellow'
                            ? '☀️'
                            : entry.feeling === 'red'
                              ? '🔥'
                              : null;
                    return (
                      <li
                        key={entry.id}
                        className={`flex items-start justify-between gap-3 py-2 text-sm ${
                          isSelfCheckIn ? 'pl-2 -ml-2 border-l-2 border-indigo-300' : ''
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                            <span className="font-medium text-gray-900">
                              {entry.value == null
                                ? '—'
                                : `${entry.value}${entry.unit || unitLabel ? ` ${entry.unit || unitLabel}` : ''}`}
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatShortDate(entry.observedOn)}
                            </span>
                            {isSelfCheckIn && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 border border-indigo-200 px-2 py-0.5 text-[11px] font-medium text-indigo-700">
                                <span aria-hidden="true">🙋</span>
                                Self check-in
                              </span>
                            )}
                            {ratingLabel && (
                              <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${ratingChipClass}`}>
                                {ratingLabel}
                              </span>
                            )}
                            {feelingEmoji && (
                              <span
                                className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-2 py-0.5 text-[11px] font-medium text-blue-800"
                                title={`Feeling: ${entry.feeling}`}
                              >
                                <span aria-hidden="true">{feelingEmoji}</span>
                                Feeling
                              </span>
                            )}
                            {entry.supportLevel && (
                              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                                {SUPPORT_LABEL[entry.supportLevel] ||
                                  entry.supportLevel}
                              </span>
                            )}
                          </div>
                          {isSelfCheckIn ? (
                            <p className="mt-0.5 text-xs text-indigo-700">
                              Entered by the student in the check-in flow.
                            </p>
                          ) : (
                            actorName && (
                              <p className="mt-0.5 text-xs text-gray-500 flex items-center gap-1 flex-wrap">
                                <span>Logged by {actorName}</span>
                                {short && (
                                  <span
                                    className={`inline-block text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${actorColor}`}
                                  >
                                    {short}
                                  </span>
                                )}
                              </p>
                            )
                          )}
                          {entry.notes && !isSelfCheckIn && (
                            <p className="mt-0.5 text-xs text-gray-500 line-clamp-2">
                              {entry.notes}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDelete(entry)}
                          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                          title="Delete this data point"
                        >
                          <Icon name="Trash2" size={14} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Recent behavior incidents linked to this goal. Only
                renders once at least one incident has been tagged
                against this goal via the "Does this relate to an IEP
                goal?" chip picker in the incident form. For social-
                emotional / behavioral goals this is the "real data"
                that used to be trapped on the Behavior Incidents page
                with no way back to the goal it moved. */}
            {linkedIncidents.length > 0 && (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4">
                <div className="mb-3 flex items-center gap-2">
                  <Icon name="Link" size={16} className="text-indigo-600" />
                  <h3 className="text-sm font-semibold text-gray-900">
                    Recent behavior incidents linked to this goal
                  </h3>
                </div>
                <ul className="divide-y divide-indigo-100">
                  {linkedIncidents.map((inc) => {
                    const actor = getUserById(inc.reportedBy);
                    const actorName =
                      actor?.displayName ||
                      deriveNameFromEmail(actor?.email) ||
                      (inc.reportedBy ? 'A teammate' : null);
                    const short = shortRoleLabel(actor);
                    const actorColor = roleColorClasses(actor);
                    const summary =
                      (inc.behaviorDescription || '').trim() ||
                      (inc.behaviorType || '').replace(/-/g, ' ');
                    return (
                      <li key={inc.id} className="py-2 text-sm">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <span className="font-medium text-gray-900 line-clamp-2">
                            {summary || 'Incident'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatShortDate(inc.occurredAt)}
                          </span>
                          {inc.hypothesizedFunction && (
                            <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs text-indigo-800">
                              function: {inc.hypothesizedFunction}
                            </span>
                          )}
                        </div>
                        {(actorName || inc.location) && (
                          <p className="mt-0.5 text-xs text-gray-500 flex items-center gap-1 flex-wrap">
                            {inc.location && <span>{inc.location}</span>}
                            {actorName && (
                              <>
                                {inc.location && <span aria-hidden>·</span>}
                                <span>Logged by {actorName}</span>
                                {short && (
                                  <span
                                    className={`inline-block text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${actorColor}`}
                                  >
                                    {short}
                                  </span>
                                )}
                              </>
                            )}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ul>
                <p className="mt-3 text-xs text-gray-500">
                  Incidents get linked here when someone taps this goal
                  in the "Does this relate to an IEP goal?" section of
                  the incident form. Log a behavior incident from the
                  sidebar to add more.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-gray-200 bg-white p-4 rounded-b-xl">
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
      </div>
    </div>
  );
};

export default GoalDetailView;
